import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';

// Functional component
function InactiveClient({ clients }) {
  const reportingClient = useSelector((state) => state.reportingClient);
  const [displayedCount, setDisplayedCount] = useState(0);
  const [targetCount, setTargetCount] = useState(0);
  const animationFrameRef = useRef(null);
  const displayedCountRef = useRef(0);

  // Validate input array
  const validClients = useMemo(() => {
    if (!Array.isArray(clients)) {
      return [];
    }
    return clients;
  }, [clients]);

  const inactiveClientsCount = useMemo(() => {
    try {
      const filteredClients = validClients.filter(client => {
        if (!client || !client._id) return false;

        const isClientDetailMatch = !reportingClient?.clientDetails || 
          (Array.isArray(reportingClient.clientDetails) && reportingClient.clientDetails.includes(client._id));
        
        const isAccountManagerMatch = !reportingClient?.clientAccountManager ||
          (Array.isArray(reportingClient.clientAccountManager) &&
            client.acc_manager &&
            client.acc_manager._id &&
            reportingClient.clientAccountManager.includes(client.acc_manager._id));
        
        const isStatusMatch = !reportingClient?.clientStatus ||
          (Array.isArray(reportingClient.clientStatus) && reportingClient.clientStatus.includes(client.is_active));
        
        return isClientDetailMatch && isAccountManagerMatch && isStatusMatch;
      });

      return filteredClients.filter(client => client && client.is_active === false).length;
    } catch (error) {
      console.error('Error calculating inactive clients:', error);
      return 0;
    }
  }, [validClients, reportingClient]);

  useEffect(() => {
    const parsedCount = parseInt(inactiveClientsCount) || 0;
    if (!isNaN(parsedCount)) {
      setTargetCount(parsedCount);
    } else {
      setTargetCount(0);
    }
  }, [inactiveClientsCount]);

  useEffect(() => {
    displayedCountRef.current = displayedCount;
  }, [displayedCount]);

  useEffect(() => {
    // Cancel any existing animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    const startValue = displayedCountRef.current;
    const endValue = targetCount;
    const duration = 1000; // Animation duration in milliseconds
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      
      const currentValue = startValue + (endValue - startValue) * easeOut;
      const newValue = Math.floor(currentValue);
      setDisplayedCount(newValue);
      displayedCountRef.current = newValue;

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        setDisplayedCount(endValue);
        displayedCountRef.current = endValue;
        animationFrameRef.current = null;
      }
    };

    if (Math.abs(startValue - endValue) > 0.01) {
      animationFrameRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [targetCount]);

  return (
    <div>
      <div className="overview-item">
        <div className="overview-title-outer">
          <div className="overview-title">
            Inactive Clients
          </div>
          <div className="overview-icon">
            <svg width="49" height="49" viewBox="0 0 49 49" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g filter="url(#filter0_d_2951_85)">
                <circle cx="24.5" cy="24.5" r="20.5" fill="white" />
              </g>
              <path d="M28.8582 15.5623H18.9443C17.1815 15.5623 15.6782 16.6883 15.1203 18.2562H13.5368C13.2881 18.2562 13.0862 18.4574 13.0862 18.7052C13.0862 18.9531 13.2881 19.1542 13.5368 19.1542H14.9139C14.8977 19.3015 14.8887 19.4514 14.8887 19.6032V21.8482H13.5368C13.2881 21.8482 13.0862 22.0493 13.0862 22.2972C13.0862 22.545 13.2881 22.7462 13.5368 22.7462H14.8887V25.8891H13.5368C13.2881 25.8891 13.0862 26.0903 13.0862 26.3381C13.0862 26.586 13.2881 26.7871 13.5368 26.7871H14.8887V29.9301H13.5368C13.2881 29.9301 13.0862 30.1312 13.0862 30.3791C13.0862 30.6269 13.2881 30.8281 13.5368 30.8281H14.8887V33.073C14.8887 33.2248 14.8977 33.3748 14.9139 33.522H13.5368C13.2881 33.522 13.0862 33.7232 13.0862 33.971C13.0862 34.2189 13.2881 34.42 13.5368 34.42H15.1203C15.6782 35.9879 17.1815 37.114 18.9443 37.114H28.8582C31.0942 37.114 32.9138 35.3009 32.9138 33.073V19.6032C32.9138 17.3753 31.0942 15.5623 28.8582 15.5623ZM32.0125 33.073C32.0125 34.8062 30.5976 36.216 28.8582 36.216H18.9443C17.2049 36.216 15.7899 34.8062 15.7899 33.073V19.6032C15.7899 17.8701 17.2049 16.4602 18.9443 16.4602H28.8582C30.5976 16.4602 32.0125 17.8701 32.0125 19.6032V33.073ZM23.9013 26.3381C25.3919 26.3381 26.605 25.1294 26.605 23.6442C26.605 22.1589 25.3919 20.9502 23.9013 20.9502C22.4106 20.9502 21.1975 22.1589 21.1975 23.6442C21.1975 25.1294 22.4106 26.3381 23.9013 26.3381ZM23.9013 21.8482C24.8953 21.8482 25.7038 22.6537 25.7038 23.6442C25.7038 24.6346 24.8953 25.4401 23.9013 25.4401C22.9071 25.4401 22.0987 24.6346 22.0987 23.6442C22.0987 22.6537 22.9071 21.8482 23.9013 21.8482ZM28.4075 31.726V32.175C28.4075 32.4229 28.2056 32.624 27.9569 32.624C27.7082 32.624 27.5063 32.4229 27.5063 32.175V31.726C27.5063 29.7451 25.8894 28.1341 23.9013 28.1341C21.9131 28.1341 20.2962 29.7451 20.2962 31.726V32.175C20.2962 32.4229 20.0943 32.624 19.8456 32.624C19.5968 32.624 19.395 32.4229 19.395 32.175V31.726C19.395 29.2503 21.4165 27.2361 23.9013 27.2361C26.386 27.2361 28.4075 29.2503 28.4075 31.726Z" fill="#6A7988" />
              <path fillRule="evenodd" clipRule="evenodd" d="M18.9444 15.4761C17.1655 15.4761 15.6456 16.5988 15.06 18.17H13.5368C13.2408 18.17 13 18.4095 13 18.7052C13 19.001 13.2408 19.2404 13.5368 19.2404H14.8188C14.8082 19.3598 14.8025 19.4809 14.8025 19.6032V21.762H13.5368C13.2408 21.762 13 22.0014 13 22.2972C13 22.5929 13.2408 22.8324 13.5368 22.8324H14.8025V25.8029H13.5368C13.2408 25.8029 13 26.0424 13 26.3381C13 26.6339 13.2408 26.8733 13.5368 26.8733H14.8025V29.8439H13.5368C13.2408 29.8439 13 30.0833 13 30.3791C13 30.6748 13.2408 30.9143 13.5368 30.9143H14.8025V33.0731C14.8025 33.1954 14.8082 33.3165 14.8188 33.4358H13.5368C13.2408 33.4358 13 33.6753 13 33.971C13 34.2668 13.2408 34.5062 13.5368 34.5062H15.06C15.6456 36.0775 17.1655 37.2002 18.9444 37.2002H28.8582C31.1415 37.2002 33 35.3489 33 33.0731V19.6032C33 17.3274 31.1415 15.4761 28.8582 15.4761H18.9444ZM15.2016 18.2852C15.7476 16.7506 17.219 15.6485 18.9444 15.6485H28.8582C31.0468 15.6485 32.8276 17.4232 32.8276 19.6032V33.0731C32.8276 35.2531 31.0468 37.0278 28.8582 37.0278H18.9444C17.219 37.0278 15.7476 35.9257 15.2016 34.3911L15.1812 34.3338H13.5368C13.3354 34.3338 13.1724 34.171 13.1724 33.971C13.1724 33.7711 13.3354 33.6083 13.5368 33.6083H15.0102L14.9996 33.5126C14.9837 33.3682 14.9749 33.2214 14.9749 33.0731V30.7419H13.5368C13.3354 30.7419 13.1724 30.579 13.1724 30.3791C13.1724 30.1792 13.3354 30.0163 13.5368 30.0163H14.9749V26.7009H13.5368C13.3354 26.7009 13.1724 26.5381 13.1724 26.3381C13.1724 26.1382 13.3354 25.9754 13.5368 25.9754H14.9749V22.66H13.5368C13.3354 22.66 13.1724 22.4971 13.1724 22.2972C13.1724 22.0973 13.3354 21.9344 13.5368 21.9344H14.9749V19.6032C14.9749 19.4549 14.9837 19.3081 14.9996 19.1637L15.0102 19.068H13.5368C13.3354 19.068 13.1724 18.9052 13.1724 18.7052C13.1724 18.5053 13.3354 18.3425 13.5368 18.3425H15.1812L15.2016 18.2852ZM15.8762 19.6032C15.8762 17.918 17.2522 16.5465 18.9444 16.5465H28.8582C30.5503 16.5465 31.9263 17.918 31.9263 19.6032V33.0731C31.9263 34.7583 30.5503 36.1298 28.8582 36.1298H18.9444C17.2522 36.1298 15.8762 34.7583 15.8762 33.0731V19.6032ZM18.9444 16.3741C17.1576 16.3741 15.7038 17.8222 15.7038 19.6032V33.0731C15.7038 34.8541 17.1576 36.3022 18.9444 36.3022H28.8582C30.6449 36.3022 32.0987 34.8541 32.0987 33.0731V19.6032C32.0987 17.8222 30.6449 16.3741 28.8582 16.3741H18.9444ZM23.9013 20.864C22.3633 20.864 21.1113 22.111 21.1113 23.6442C21.1113 25.1774 22.3633 26.4244 23.9013 26.4244C25.4392 26.4244 26.6912 25.1774 26.6912 23.6442C26.6912 22.111 25.4392 20.864 23.9013 20.864ZM21.2837 23.6442C21.2837 22.2068 22.4579 21.0364 23.9013 21.0364C25.3446 21.0364 26.5188 22.2068 26.5188 23.6442C26.5188 25.0815 25.3446 26.2519 23.9013 26.2519C22.4579 26.2519 21.2837 25.0815 21.2837 23.6442ZM22.1849 23.6442C22.1849 22.7016 22.9545 21.9344 23.9013 21.9344C24.848 21.9344 25.6176 22.7016 25.6176 23.6442C25.6176 24.5868 24.848 25.3539 23.9013 25.3539C22.9545 25.3539 22.1849 24.5868 22.1849 23.6442ZM23.9013 21.762C22.8599 21.762 22.0125 22.6058 22.0125 23.6442C22.0125 24.6826 22.8599 25.5264 23.9013 25.5264C24.9427 25.5264 25.79 24.6826 25.79 23.6442C25.79 22.6058 24.9427 21.762 23.9013 21.762ZM19.4812 31.7261C19.4812 29.2982 21.4638 27.3223 23.9013 27.3223C26.3387 27.3223 28.3213 29.2982 28.3213 31.7261V32.1751C28.3213 32.2504 28.2982 32.3204 28.2586 32.3785V32.3727C28.2586 32.087 28.027 31.8554 27.7414 31.8554H27.5925V31.7261C27.5925 29.6972 25.9367 28.0479 23.9013 28.0479C21.8658 28.0479 20.21 29.6972 20.21 31.7261V31.8554H19.9828C19.7465 31.8554 19.5473 32.0138 19.4854 32.2302C19.4826 32.2122 19.4812 32.1938 19.4812 32.1751V31.7261ZM19.5173 32.5984C19.3905 32.5006 19.3088 32.3474 19.3088 32.1751V31.7261C19.3088 29.2024 21.3692 27.1499 23.9013 27.1499C26.4333 27.1499 28.4937 29.2024 28.4937 31.7261V32.1751C28.4937 32.3968 28.3584 32.5868 28.166 32.6681C28.0725 32.8022 27.9172 32.8899 27.7414 32.8899H19.9828C19.778 32.8899 19.6011 32.771 19.5173 32.5984ZM20.3825 31.8554H27.4201V31.7261C27.4201 29.793 25.8421 28.2203 23.9013 28.2203C21.9604 28.2203 20.3825 29.793 20.3825 31.7261V31.8554Z" fill="#6A7988" />
              <path d="M32 21C34.7614 21 37 18.7614 37 16C37 13.2386 34.7614 11 32 11C29.2386 11 27 13.2386 27 16C27 18.7614 29.2386 21 32 21Z" fill="white" stroke="#6A7988" />
              <path d="M30 18L34 14" stroke="#6A7988" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M34 18L30 14" stroke="#6A7988" strokeLinecap="round" strokeLinejoin="round" />
              <defs>
                <filter id="filter0_d_2951_85" x="0" y="0" width="49" height="49" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                  <feOffset />
                  <feGaussianBlur stdDeviation="2" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix type="matrix" values="0 0 0 0 0.468733 0 0 0 0 0.51145 0 0 0 0 0.554167 0 0 0 0.39 0" />
                  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2951_85" />
                  <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2951_85" result="shape" />
                </filter>
              </defs>
            </svg>

          </div>
        </div>

        <div className="overview-rice">
          {displayedCount}
        </div>
      </div>

    </div>
  );
}

export default InactiveClient;
